
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { SportType } from '@/types/sport';

export interface Club {
  id: string;
  name: string;
  website?: string | null;
  location?: string | null;
  sport?: SportType | null;
  organization_id: string;
  created_at: string;
  organization_name?: string;
  user_role?: string;
  profile_picture_url?: string | null;
}

export const useClubs = () => {
  const [clubs, setClubs] = useState<Club[]>([]);
  const [loading, setLoading] = useState(true);
  const { profile } = useAuth();

  const fetchClubs = useCallback(async () => {
    try {
      setLoading(true);
      
      if (!profile?.id) {
        setClubs([]);
        return;
      }

      const { data, error } = await supabase
        .from('user_club_roles')
        .select(`
          role,
          clubs!inner (
            id,
            name,
            website,
            location,
            sport,
            organization_id,
            created_at,
            profile_picture_url,
            organizations!inner (
              name
            )
          )
        `)
        .eq('user_id', profile.id)
        .eq('is_active', true);


      if (error) {
        console.error('Error fetching user clubs:', error);
        toast.error(`Failed to load clubs: ${error.message}`);
        return;
      }

      // Transform the data to match our Club interface
      const transformedClubs: Club[] = (data || []).map((item: { role: string; clubs: { id: string; name: string; website?: string; location?: string; sport?: string; organization_id: string; created_at: string; profile_picture_url?: string; organizations: { name: string } } }) => ({
        id: item.clubs.id,
        name: item.clubs.name,
        website: item.clubs.website || null,
        location: item.clubs.location || null,
        sport: item.clubs.sport as SportType || null,
        organization_id: item.clubs.organization_id,
        created_at: item.clubs.created_at,
        profile_picture_url: item.clubs.profile_picture_url || null,
        organization_name: item.clubs.organizations.name,
        user_role: item.role
      }));

      setClubs(transformedClubs);
    } catch (error) {
      console.error('Error fetching user clubs:', error);
      toast.error('Failed to load clubs');
    } finally {
      setLoading(false);
    }
  }, [profile?.id]);

  useEffect(() => {
    if (profile?.id) {
      fetchClubs();
    }
  }, [profile?.id, fetchClubs]);

  return {
    clubs,
    loading,
    fetchClubs
  };
};
